import { useState, useEffect, ReactNode } from "react";
import { supabase } from "@lib/supabase/client";
import { Construction, Wrench } from "lucide-react";
import { PlatformLogo } from "@shared/components/common/PlatformLogo";
import { useAuth } from "@app/providers/AuthProvider";

interface MaintenanceModeProps {
  children: ReactNode;
}

const MaintenanceMode = ({ children }: MaintenanceModeProps) => {
  const { user, loading: authLoading } = useAuth();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMaintenanceAndAdmin();
  }, [authLoading, user]);

  const checkMaintenanceAndAdmin = async () => {
    try {
      // Check maintenance mode setting
      const { data: settingData } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'maintenance_mode')
        .maybeSingle();

      const maintenanceEnabled = (settingData?.setting_value as Record<string, unknown> | null)?.enabled || false;
      setIsMaintenanceMode(Boolean(maintenanceEnabled));

      // If maintenance is enabled, check if user is admin
      if (maintenanceEnabled) {
        if (authLoading) return;

        if (user) {
          const { data: isAdminData } = await supabase.rpc('has_role', {
            _user_id: user.id,
            _role: 'admin'
          });
          setIsAdmin(isAdminData || false);
        }
      }
    } catch (error) {
      console.error('Error checking maintenance mode:', error);
    }
    setLoading(false);
  };

  // Show loading state briefly
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <PlatformLogo variant="transparent" className="h-12" />
        </div>
      </div>
    );
  }

  // If maintenance mode is on and user is not admin, show maintenance page
  if (isMaintenanceMode && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4" dir="rtl">
        <div className="text-center max-w-md">
          {/* Logo */}
          <PlatformLogo variant="transparent" className="h-16 mx-auto mb-8" />
          
          {/* Icon */}
          <div className="relative mb-8">
            <div className="w-24 h-24 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Construction className="w-12 h-12 text-primary animate-pulse" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Wrench className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-foreground mb-4">
            المنصة تحت الصيانة
          </h1>
          
          {/* Description */}
          <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
            نعمل حالياً على تحسين المنصة لتقديم تجربة أفضل لكم.
            <br />
            نعتذر عن أي إزعاج وسنعود قريباً!
          </p>
          
          {/* Decorative elements */}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </div>
          
          {/* Footer */}
          <p className="mt-8 text-xs text-muted-foreground">
            © {new Date().getFullYear()} DietiTech - جميع الحقوق محفوظة
          </p>
        </div>
      </div>
    );
  }

  // Otherwise, show the normal app
  return <>{children}</>;
};

export default MaintenanceMode;
